import { useEffect, useMemo, useState } from 'react';

import { ActionItem, LeaseLifecycle } from '@urbanmix-tech/shared-js';

import { useAuth } from '@/shared/context/AuthProvider';

import {
  setApartmentData,
  setBuildingVisual,
  setHighlightedUnits,
  setVisualizer,
} from '@/features/project/projectSlice';
import { ColorGradientVisualizer } from '@urbanmix-tech/um-embedded';
import { useDispatch } from 'react-redux';
import { ActionGroupList } from './TrackerActionGroupList';
import { StateVisualizer } from './BuildingVisualizers';
import { FormControl, MenuItem, Select } from '@mui/material';
import { convertPseudoDateToRealDate } from '@/helpers/common';

type MyDashboardTabContentProps = {
  actionItems: ActionItem[];
  leaseStatusFilter: LeaseLifecycle[];
  onClickActionItem(actionItem: ActionItem): void;
  onBuildingDataChange?: (data: Record<string, any>) => void;
  isLoading?: boolean;
};

export function MyDashboardTabContent({
  actionItems,
  onClickActionItem,
  onBuildingDataChange,
  isLoading = false,
}: MyDashboardTabContentProps) {
  const { userData } = useAuth();
  const dispatch = useDispatch();

  const [sortOrder, setSortOrder] = useState<string>('createdAt');

  const userActionItems = useMemo(() => {
    const filteredActionItems = actionItems.filter((action) => {
      if (action.status === 'ignored')  {
        return false;
      }
      if (action.status === 'done' && action.completedOn == null) {
        return false;
      }

      if (action.owner?.userId === userData?.id) {
        return true;
      }
      if (action.supervisor?.userId === userData?.id) {
        return true;
      }
      if (
        action.followers?.some((follower) => follower.userId === userData?.id)
      ) {
        return true;
      }

      if (!action.owner) {
        return true;
      }
      return false;
    });
    return filteredActionItems;
  }, [userData, actionItems]);

  const filteredActionItems = useMemo(() => {
    return userActionItems.filter((actionItem) => {
      if (!actionItem.completedOn || actionItem.status == 'active') {
        return true;
      }

      // check if action Item completed more than 1 minute ago and return false
      const today = new Date();
      const completedAt: Date | null = convertPseudoDateToRealDate(
        actionItem.completedOn
      );

      if (completedAt && (today.getTime() - completedAt.getTime()) > 60000) {
        return false;
      }
      return true;
    });
  }, [userActionItems]);

  useEffect(() => {
    // every action item has a unit associated with it
    // i want a set that contains unique units from all action items
    const distinctUnits = new Set<string>();

    filteredActionItems.forEach((actionItem) => {
      if (actionItem.unitId) {
        distinctUnits.add(actionItem.unitId);
      }
    });
    // make an array of units from the set
    const units = Array.from(distinctUnits);

    // now lets figure out what color for each unit
    const data = units.map((unit) => {
      const items = filteredActionItems.filter((actionItem) => {
        return actionItem.unitId === unit;
      });
      let state = items.reduce((acc, item) => {
        // check if due day passed
        const today = new Date();
        const dueDate = convertPseudoDateToRealDate(item.dueDate);
        let state = acc;
        if (dueDate && dueDate < today && item.status !== 'done') {
          state = 'late';
        } else {
          state = 'on time';
        }

        if (item.status === 'done') {
          state = 'done';
        }
        // console.log('old state', acc, 'new state', state);
        if (state === 'late') {
          return 'late';
        } else if (state === 'done' && acc !== 'late') {
          return 'done';
        } else if (state === 'on time' && acc !== 'late' && acc !== 'done') {
          return 'on time';
        }

        return acc;
      }, '');
      let apartmentData = {
        apartmentId: unit,
        value: state,
        label: 'Status',
      };
      // console.log('apartmentData', apartmentData);
      return apartmentData;
    });

    dispatch(setHighlightedUnits(null));
    console.log('dashboard set visualizer');
    dispatch(
      setBuildingVisual({
        apartmentData: data,
        visualizer: new StateVisualizer(),
    })
    );
  }, [filteredActionItems, dispatch]);

  return (
    <div className="flex-1 overflow-auto p-[15px]">
      <FormControl>
        <Select
          value={sortOrder}
          onChange={(e) => {
            setSortOrder(e.target.value);
          }}
          size="xs"
          variant="standard"
        >
          <MenuItem value="dueDate">Sort by Due Date</MenuItem>
          <MenuItem value="createdAt">Sort by Newest</MenuItem>
        </Select>
      </FormControl>
      <ActionGroupList
        actionItems={filteredActionItems}
        onClickActionItem={onClickActionItem}
        onBuildingDataChange={onBuildingDataChange}
        sortOrder={sortOrder}
        isLoading={isLoading}
      />
    </div>
  );
}
